import { api } from "@/lib/api";

/**
 * Ask the browser for the user's coordinates.
 * Resolves with { latitude, longitude, accuracy } on success, or `null` if
 * the user denies permission / browser cannot fetch. Never throws.
 */
export function getBrowserPosition({ timeout = 10000, maximumAge = 5 * 60 * 1000 } = {}) {
  return new Promise((resolve) => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      resolve(null);
      return;
    }
    let done = false;
    const finish = (v) => {
      if (done) return;
      done = true;
      resolve(v);
    };
    try {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const { latitude, longitude, accuracy } = pos.coords || {};
          if (typeof latitude !== "number" || typeof longitude !== "number") {
            finish(null);
            return;
          }
          finish({ latitude, longitude, accuracy });
        },
        () => finish(null),
        { enableHighAccuracy: false, timeout, maximumAge },
      );
    } catch {
      finish(null);
    }
    // safety net in case the callback never fires
    setTimeout(() => finish(null), timeout + 500);
  });
}

/**
 * Capture coords from browser AND send to backend. Silent failure on denial.
 * Returns { latitude, longitude } on success, null otherwise.
 */
export async function captureAndSaveLocation() {
  const p = await getBrowserPosition();
  if (!p) return null;
  try {
    await api.post("/profile/location", {
      latitude: p.latitude,
      longitude: p.longitude,
    });
    return { latitude: p.latitude, longitude: p.longitude };
  } catch {
    return null;
  }
}
